// src/components/group/ui/MeetingDetailView.jsx
import "./MeetingDetailView.css";
import { useState, useEffect } from "react";
import { getMeetingAssignees } from "../../../../../../api/groupApi";
import { formatNice } from "../../../../../../utils/formatNice";
import TableScroll from "../../../../../../components/TableScroll/TableScroll";

export default function MeetingDetailView({
  meeting,           // Full meeting object from meetings list
  organizationId,
  groupId,
  onBack,
  onEdit
}) {
  const [assignees, setAssignees] = useState([]);
  const [loadingAssignees, setLoadingAssignees] = useState(true);
  const [error, setError] = useState("");

  const meetingId = meeting?.id;

  useEffect(() => {
    if (!meetingId || !organizationId || !groupId) {
      setLoadingAssignees(false);
      return;
    }

    async function loadAssignees() {
      setLoadingAssignees(true);
      setError("");
      try {
        const data = await getMeetingAssignees(organizationId, groupId, meetingId);
        setAssignees(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("Failed to load assignees:", err);
        setError("Could not load assignees");
      } finally {
        setLoadingAssignees(false);
      }
    }

    loadAssignees();
  }, [meetingId, organizationId, groupId]);

  if (!meeting) {
    return (
      <div className="gd-meeting-detail">
        <button className="gd-back" onClick={onBack}>← Back to Meetings</button>
        <div className="gd-muted" style={{ marginTop: 10 }}>Meeting not found.</div>
      </div>
    );
  }

  // Determine display title (custom or predefined)
// Determine display title (no custom fields anymore)
const displayTitle = formatNice(meeting.meetingType || "Practice Meeting");


  // Combine goals
  const allGoals = [...(meeting.goals || []), ...(meeting.customGoals || [])];
  const goalText = allGoals.length > 0 ? allGoals.join(" • ") : "—";

  // Format dates
  const createdDate = new Date(meeting.createdAt).toLocaleString();

  const assignedCount = assignees.length;
  // You can enhance this later with real completion status from assignments table
  const completedCount = 0; // Placeholder — update when you track completion

  return (
    <div className="gd-meeting-detail">
      <div className="gd-meeting-top">
        <button className="gd-back" onClick={onBack}>
          ← Back to Meetings
        </button>
      </div>

      <div className="gd-meeting-header">
        <div>
          <h3 className="gd-meeting-title">{displayTitle}</h3>
          <div className="gd-subline">
            <span className="gd-state active">Active</span>
            <span className="gd-dot">•</span>
            <span className="gd-muted">Created: {createdDate}</span>
            <span className="gd-dot">•</span>
            <span className="gd-muted">
              Assigned to {assignedCount} {assignedCount === 1 ? "person" : "people"}
              {assignedCount > 0 && completedCount > 0 && ` (${completedCount} completed)`}
            </span>
          </div>
        </div>

        <div className="gd-meeting-actions">
        <button className="gd-secondary" onClick={onEdit}>
          Rename
        </button>
  
        </div>
      </div>

      <div className="gd-meeting-meta">
 

        <div className="gd-meta-card">
          <div className="gd-meta-label">Industry</div>
          <div className="gd-meta-value">
          {formatNice(meeting.industry || "—")}
          </div>
        </div>

        <div className="gd-meta-card">
          <div className="gd-meta-label">Assignees Role</div>
          <div className="gd-meta-value">
            { formatNice(meeting.userRole || "—")}
          </div>
        </div>

        <div className="gd-meta-card">
          <div className="gd-meta-label">Speaking With</div>
          <div className="gd-meta-value">
            {formatNice(meeting.speakingWith || "—")}
          </div>
        </div>

        <div className="gd-meta-card">
          <div className="gd-meta-label">Goals</div>
          <div className="gd-meta-value">{goalText}</div>
        </div>

        <div className="gd-meta-card">
          <div className="gd-meta-label">Duration</div>
          <div className="gd-meta-value">{meeting.duration} min</div>
        </div>

        <div className="gd-meta-card">
          <div className="gd-meta-label">AI Behavior</div>
          <div className="gd-meta-value">
            {formatNice(meeting.aiBehavior)}
          </div>
        </div>
      </div>

      <div className="gd-section-title" style={{ marginTop: 20 }}>Assignees</div>

      {loadingAssignees ? (
        <div className="gd-muted">Loading assignees…</div>
      ) : error ? (
        <div className="gd-error">{error}</div>
      ) : assignees.length === 0 ? (
        <div className="gd-empty-row">
          No one assigned yet. Click <strong>Assign More</strong> to add members.
        </div>
      ) : (
        <TableScroll>

             <div className="gd-table gd-assignee-table">
          <div className="gd-row gd-head gd-row-assignees">
            <div>Assignee</div>
            <div>Scheduled</div>
            <div>Assigned By</div>
            <div>Status</div>
          </div>

          {assignees.map((a) => {
            const fullName = a.fullName || `${a.firstName || ""} ${a.lastName || ""}`.trim() || a.email;

            return (
              <div className="gd-row gd-row-assignees" key={a.id}>
                <div className="gd-strong">{fullName}</div>
                <div className="gd-muted">
                  {a.scheduledFor ? new Date(a.scheduledFor).toLocaleString() : "—"}
                </div>
                <div className="gd-muted">{a.assignedByName || "—"}</div>
                <div>
                  <span className="gd-state invited">Assigned</span>
                  {/* Update later when you track completion/feedback */}
                </div>
              
              </div>
            );
          })}
        </div>
        </TableScroll>
     
      )}
    </div>
  );
}